import {
  addDomain, addNavigationSettings,
  addSubscription,
  addUser,
  removeUser,
  setSelectedAttendanceWeek
} from "../actions/actions";
import {addTranslationForLanguage, initialize, setActiveLanguage} from "react-localize-redux";
import { renderToStaticMarkup } from 'react-dom/server';
import {serviceLocator} from "../infrastructure/ServiceLocator";
import DomainHelper from "../infrastructure/DomainHelper";
import LayoutHelper from "../infrastructure/LayoutHelper";
import moment from "moment";
import {NAVIGATION_SETTINGS_CONFIG} from "../constants/organizationConfigurations";

const LOCAL_STORAGE_LANG_KEY = 'voopty-lang';
const LOCALE_EN = 'en_US';
const LOCALE_UK = 'uk_UA';
const LOCALE_RU = 'ru_UA';

const defaultLocales = [LOCALE_EN, LOCALE_UK, LOCALE_RU];
const localeImports = {
  [LOCALE_EN]: () => import(/* webpackChunkName: "locale-en" */ '../en.json'),
  [LOCALE_UK]: () => import(/* webpackChunkName: "locale-uk", webpackPreload: true */ '../uk.json'),
  [LOCALE_RU]: () => import(/* webpackChunkName: "locale-ru" */ '../ru.json'),
};

function DomainManager(store, environment) {
  const isBusinessEnv = environment === 'business';

  async function initDomain(initialState, callback) {
    try {
      const defaultDomainConfig = initialState?.domain;
      const domainConfig = await getDomainConfig(defaultDomainConfig);
      store.dispatch(addDomain(domainConfig));

      const locales =
        (isBusinessEnv && domainConfig?.cultures) || defaultLocales;
      const defaultLocaleCode = domainConfig?.defaultCulture || LOCALE_EN;
      initLocale(locales, defaultLocaleCode);

      await initUser(callback, domainConfig);
    } catch (e) {
      console.error('Init domain error:', e);
    }
  }

  async function initSubscription(user) {
    const subscription = await serviceLocator.userSubscriptionApiCall.getSubscriptionInfo(user._id)
    dispatchSubscriptionUpdate(subscription);
  }

  async function initNavigationSettings(user) {
    const navigationSettings = await serviceLocator.organizationConfigurationApiCall.getOrganizationConfigurationByName(user._id, NAVIGATION_SETTINGS_CONFIG);
    dispatchNavigationSettingsUpdate(navigationSettings.value);
  }

  async function getDomainConfig(defaultConfig) {
    if (defaultConfig) {
      return defaultConfig;
    }

    try {
      return serviceLocator.checkDomain(window.location.hostname);
    } catch (e) {
      console.error('Failed to get domain config:', e);
      return null;
    }
  }

  function initLocale(locales, defaultLocale) {
    const locale = getBrowserLocale(defaultLocale);
    const initializeAction = initialize(
      {
        languages: locales,
        options: {
          renderToStaticMarkup,
          defaultLanguage: locale,
          renderInnerHtml: true
        },
    });
    store.dispatch(initializeAction);

    loadTranslations(locale);
    updateLocale(locale);

    // TODO: fix async issue with locale change
    queueMicrotask(() => {
      updateLocale(locale);
    });
  }

  function updateLocale(localeCode) {
    const api = serviceLocator.userApi;
    api.setLang(localeCode);
    store.dispatch(setActiveLanguage(localeCode));
  }

  function loadTranslations(activeLocale) {
    const loadLocale = async (localeCode, retries = 2) => {
      try {
        const { default: json } = await localeImports[localeCode]();
        store.dispatch(addTranslationForLanguage(json, localeCode));
      } catch (e) {
        console.error(`Failed to load translations for ${localeCode}:`, e);
        if (retries > 0) {
          setTimeout(() => loadLocale(localeCode, retries - 1), 500);
        }
      }
    };

    const others = Object.keys(localeImports).filter(l => l !== activeLocale);

    loadLocale(activeLocale);
    others.forEach((l) => loadLocale(l));
  }

  function getBrowserLocale(defaultLocale) {
    let locale = defaultLocale;
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_LANG_KEY);
      // Guard against a stale/legacy locale code left over in localStorage
      // (e.g. from an older format) that no longer matches a known locale -
      // otherwise loadTranslations() would keep failing to find it forever.
      if (stored && localeImports[stored]) {
        locale = stored;
      }
    } catch (e) {
      console.log('Failed to get language from local storage:', e);
    }
    return locale || defaultLocale;
  }

  async function initUser(callback, domainConfig) {
    try {
      if (isBusinessEnv) {
        const user = await initBusinessUser(domainConfig);
        if (user) {
          await initSubscription(user);
          await initNavigationSettings(user);
        }
        selectCurrentAttendanceWeek(store);
      } else {
        const isGelios = /gelios/i.test(domainConfig.staticUrl);
        await initClientUser(isGelios);
      }
    } catch (e) {
      console.error('Init user error:', e);
      LayoutHelper.onErr(e);
    } finally {
      callback();
    }
  }

  async function initClientUser(isGelios) {
    const user = await serviceLocator.checkClientUser(isGelios);
    dispatchUserUpdate(user);
  }

  async function initBusinessUser(domainConfig) {
    const user = await getBusinessUser(domainConfig);
    dispatchUserUpdate(user);
    return user;
  }

  async function getBusinessUser(domainConfig) {
    const user = await serviceLocator.checkUser();
    if (!user) {
      return null;
    }
    serviceLocator.initBizUser(store.dispatch);
    user.clientDomain = DomainHelper.getClientDomain(user, domainConfig?.domainsToAlternate);
    return user;
  }

  function dispatchUserUpdate(user) {
    if (user) {
      store.dispatch(addUser(user));
    } else {
      store.dispatch(removeUser());
    }
  }

  function dispatchSubscriptionUpdate(subscription) {
    store.dispatch(addSubscription(subscription))
  }

  function dispatchNavigationSettingsUpdate(navigationSettings) {
    store.dispatch(addNavigationSettings(navigationSettings))
  }

  function selectCurrentAttendanceWeek(store) {
    const startOfWeekDate = moment.utc().startOf('isoWeek').format('DDMMYYYY');
    store.dispatch(setSelectedAttendanceWeek(startOfWeekDate));
  }

  return {
    initDomain,
    initUser,
  }
}

export default DomainManager;
